import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Col, Input, InputNumber, Row, Select, Spin, Typography, message } from 'antd';
import styled from 'styled-components';

import {
  useApproveAgreementPricing,
  useGetAgreementPricing,
  useRejectAgreementPricing,
  useSubmitAgreementPricing,
} from '~/controllers/agreementPricing';
import { getRoles } from '~/utils/getDecodedJwt';
import {
  AgreementPricingProducts,
  CreditCardFeeMode,
  PricingFieldMeta,
  PricingProductType,
} from '~/types/ksa/AgreementPricing';
import { useHandbook } from '~/controllers/handbook';
import { useDirection } from '~/hooks/i18nHooks';
import { DirectionProp } from '~/types/Direction';

const CREDIT_CARD_KEY = 'creditCardPerTransaction';



type FieldPatch = Partial<{
  amount: number | null;
  percent: number | null;
  feeMode: CreditCardFeeMode;
}>;

const AgreementPricingForm: FC = () => {
  const { t } = useTranslation('ksaContract');
  const dir = useDirection();

  const { data, isLoading, isError } = useGetAgreementPricing();
  const submitMutation = useSubmitAgreementPricing();
  const approveMutation = useApproveAgreementPricing();
  const rejectMutation = useRejectAgreementPricing();
  const handbook = useHandbook();

  /**
    * Options for the Credit Card fee-mode selector.
    * Sourced from the handbook when available, otherwise falls back to the
    * fixed CreditCardFeeMode enum so older applications pinned to a config
    * version whose handbook predates this key still render the options.
    */
  const creditCardFeeModeOptions = useMemo(() => {
    const fromHandbook = handbook.data?.handbooks
      ?.find((h) => h.key === 'creditCardFeeMode')
      ?.values?.map((item) => ({
        value: item.apiValue as CreditCardFeeMode,
        label: item.localizedValue,
      }));

    if (fromHandbook && fromHandbook.length > 0) return fromHandbook;

    return [
      { value: 'PERCENTAGE_ONLY' as CreditCardFeeMode, label: t('Percentage only') },
      { value: 'FIXED_ONLY' as CreditCardFeeMode, label: t('Fixed only') },
      { value: 'PERCENTAGE_PLUS_FIXED' as CreditCardFeeMode, label: t('Percentage plus fixed') },
    ];
  }, [handbook.data, t]);

  /**
   * Options for the nogo-manager approve/reject selector.
   * Sourced from the handbook when available, otherwise falls back to the
   * fixed APPROVE/REJECT decision values so older applications pinned to a
   * config version whose handbook predates this key still render the options.
   */
  const pricingDecisionOptions = useMemo(() => {
    const fromHandbook = handbook.data?.handbooks
      ?.find((h) => h.key === 'agreementPricingDecision')
      ?.values?.map((item) => ({
        value: item.apiValue as 'APPROVE' | 'REJECT',
        label: item.localizedValue,
      }));

    if (fromHandbook && fromHandbook.length > 0) return fromHandbook;

    return [
      { value: 'APPROVE' as const, label: t('Approve') },
      { value: 'REJECT' as const, label: t('Reject') },
    ];
  }, [handbook.data, t]);
  const isNogoManager = getRoles().includes('nogo-manager');

  const [values, setValues] = useState<AgreementPricingProducts>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [decision, setDecision] = useState<'APPROVE' | 'REJECT' | undefined>(undefined);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (data?.pricing?.products) {
      setValues(JSON.parse(JSON.stringify(data.pricing.products)));
      setErrors({});
    }
  }, [data]);

  const thresholdMap = useMemo(() => {
    const map: Record<string, PricingFieldMeta> = {};
    (data?.thresholds ?? []).forEach((meta) => {
      map[`${meta.productType}:${meta.fieldKey}`] = meta;
    });
    return map;
  }, [data]);

  const formEditable = Boolean(data?.editable);
  // Once the agent submits, the override is PENDING with the nogo-manager - the agent's copy
  // locks until a decision is made. It re-opens automatically after APPROVED/REJECTED, when
  // pricingManagerAssignmentService.returnToOriginalAgent hands the application back to them.
  const agentCanEditFees = formEditable && data?.status !== 'PENDING';
  const keyOf = useCallback(
    (product: PricingProductType, key: string) => `${product}:${key}`,
    [],
  );

  /** Returns a human-readable error string for a single field, or undefined if valid. */
  const validateField = useCallback(
    (product: PricingProductType, key: string): string | undefined => {
      const meta = thresholdMap[keyOf(product, key)];
      if (!meta || !meta.editable) return undefined;
      const value = values[product]?.[key];
      const min = meta.minAmount;
      const minP = meta.minPercent;
      const currency = meta.currency ?? 'SAR';

      switch (meta.fieldType) {
        case 'FIXED': {
          if (key === CREDIT_CARD_KEY) return undefined;
          if (value?.amount == null) return t('This field is required.');
          if (!Number.isInteger(value.amount)) return t('Enter a whole number — decimals are not allowed.');
          if (min != null && value.amount < min)
            return t('Must be at least {{min}} {{currency}}.', { min, currency });
          return undefined;
        }
        case 'PERCENTAGE': {
          if (value?.percent == null) return t('This field is required.');
          if (minP != null && value.percent < minP)
            return t('Must be at least {{min}}%.', { min: minP });
          return undefined;
        }
        case 'PERCENTAGE_PLUS_FIXED': {
          const mode: CreditCardFeeMode = value?.feeMode ?? 'PERCENTAGE_PLUS_FIXED';
          const percentOk =
            mode === 'FIXED_ONLY' ||
            (value?.percent != null && (minP == null || value.percent >= minP));
          const amountOk =
            mode === 'PERCENTAGE_ONLY' ||
            (value?.amount != null && (min == null || value.amount >= min));
          if (!percentOk && !amountOk)
            return t('Both fields are below the minimum — at least {{minP}}% and {{min}} {{currency}}.', { minP: minP ?? 0, min: min ?? 0, currency });
          if (!percentOk)
            return t('Percentage must be at least {{min}}%.', { min: minP ?? 0 });
          if (!amountOk)
            return t('Amount must be at least {{min}} {{currency}}.', { min: min ?? 0, currency });
          return undefined;
        }
        default:
          return undefined;
      }
    },
    [values, thresholdMap, t, keyOf],
  );

  const updateValue = (product: PricingProductType, key: string, patch: FieldPatch) => {
    setValues((prev) => {
      const next = { ...prev };
      const productValues = { ...(next[product] ?? {}) };
      productValues[key] = { ...(productValues[key] ?? {}), fieldKey: key, ...patch };
      next[product] = productValues;
      return next;
    });
    // Clear the error for this field when user starts editing
    setErrors((prev) => {
      const errId = keyOf(product, key);
      if (!prev[errId]) return prev;
      const { [errId]: _, ...rest } = prev;
      return rest;
    });
  };

  /** Validate a single field on blur and update its error in state. */
  const handleBlur = useCallback(
    (product: PricingProductType, key: string) => {
      const errMsg = validateField(product, key);
      const errId = keyOf(product, key);
      setErrors((prev) => {
        if (errMsg) return { ...prev, [errId]: errMsg };
        const { [errId]: _, ...rest } = prev;
        return rest;
      });
    },
    [validateField],
  );

  /** Ref to the card root so we can scroll to the first erring field. */
  const cardRef = useRef<HTMLDivElement>(null);

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    (Object.keys(values) as PricingProductType[]).forEach((product) => {
      const productValues = values[product] ?? {};
      Object.keys(productValues).forEach((key) => {
        const errMsg = validateField(product, key);
        if (errMsg) nextErrors[keyOf(product, key)] = errMsg;
      });
    });
    setErrors(nextErrors);

    // Scroll to the first field that has an error
    if (Object.keys(nextErrors).length > 0) {
      const firstErrId = Object.keys(nextErrors)[0];
      const el = cardRef.current?.querySelector(`[data-error-id="${firstErrId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    submitMutation.mutate(
      { products: values },
      {
        onSuccess: () => {
          void message.success(t('Pricing submitted for Sales Manager approval'));
        },
        onError: (err: any) => {
          const backendMsg =
            err?.response?.data?.description || err?.response?.data?.message;
          void message.error(backendMsg || t('Failed to submit pricing'));
        },
      },
    );
  };

  const handleApprove = () => {
    if (!validate()) return;
    approveMutation.mutate(
      { products: values },
      {
        onSuccess: () => {
          void message.success(t('Pricing approved. The merchant has been notified.'));
        },
        onError: (err: any) => {
          const backendMsg = err?.response?.data?.description || err?.response?.data?.message;
          void message.error(backendMsg || t('Failed to approve pricing'));
        },
      },
    );
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      void message.error(t('A rejection reason is required'));
      return;
    }
    rejectMutation.mutate(
      { rejectionReason: rejectionReason.trim() },
      {
        onSuccess: () => {
          void message.success(
            t('Pricing rejected. The amendment has been returned to the agent.'),
          );
          setDecision(undefined);
          setRejectionReason('');
        },
        onError: (err: any) => {
          const backendMsg = err?.response?.data?.description || err?.response?.data?.message;
          void message.error(backendMsg || t('Failed to reject pricing'));
        },
      },
    );
  };

  if (isLoading) {
    return (
      <Card dir={dir}>
        <Title>{t('Amend Agreement Pricing')}</Title>
        <Spin />
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card dir={dir}>
        <Title>{t('Amend Agreement Pricing')}</Title>
        <Alert
          type="warning"
          showIcon
          message={t('Agreement pricing is not available for this application.')}
        />
      </Card>
    );
  }

  const products = Object.keys(values) as PricingProductType[];

  if (products.length === 0) {
    return (
      <Card dir={dir}>
        <Title>{t('Amend Agreement Pricing')}</Title>
        <Alert
          type="info"
          showIcon
          message={t('No editable pricing fields available for the selected product(s).')}
        />
      </Card>
    );
  }

  return (
    <Card ref={cardRef} dir={dir}>
      <Title>{t('Amend Agreement Pricing')}</Title>

      {data.status === 'PENDING' && (
        <StatusAlert
          type="info"
          showIcon
          message={
            isNogoManager
              ? t('Pricing submitted and pending approval. You may amend fees before approving or rejecting.')
              : t('Pricing submitted and pending Sales Manager approval. Fields are locked until a decision is made.')
          }
        />
      )}
      {data.status === 'APPROVED' && (
        <>
          {data.editable ? <StatusAlert
            type="success"
            showIcon
            message={t('Pricing approved. You may still amend fees and resubmit for another review.')}
          /> : ""}
        </>
      )}
      {data.status === 'REJECTED' && (
        <StatusAlert
          type="warning"
          showIcon
          message={data.rejectionReason || t('Pricing was rejected. You may amend and resubmit.')}
        />
      )}

      {products.map((product) => (
        <ProductBlock key={product}>
          <ProductTitle>{t(product)}</ProductTitle>
          {
            Object.keys(values[product] ?? {}).map((key) => {
              const meta = thresholdMap[keyOf(product, key)];
              const value = values[product]?.[key];
              // nogo-manager only ever has access to this application while the override is
              // PENDING and assigned to them (they lose access once it's approved/rejected and
              // reassigned back to the agent), so their edit window is naturally PENDING-only.
              // The agent's window is the inverse: editable except while PENDING with the manager.
              const canEditFees = isNogoManager ? data.status === 'PENDING' : agentCanEditFees;
              const disabled = !canEditFees || !(meta?.editable ?? true);
              const currency = meta?.currency ?? 'SAR';
              const label = t(key);
              const errId = keyOf(product, key);
              const fieldType = meta?.fieldType ?? 'FIXED';
              const mode: CreditCardFeeMode = value?.feeMode ?? 'PERCENTAGE_PLUS_FIXED';
              const showPercent =
                fieldType === 'PERCENTAGE' ||
                (fieldType === 'PERCENTAGE_PLUS_FIXED' && mode !== 'FIXED_ONLY');
              const showAmount =
                fieldType === 'FIXED' ||
                (fieldType === 'PERCENTAGE_PLUS_FIXED' && mode !== 'PERCENTAGE_ONLY');

              // ── Credit Card per transaction: sub-panel layout ─────────────
              if (key === CREDIT_CARD_KEY && fieldType === 'PERCENTAGE_PLUS_FIXED') {
                return (
                  <FieldRow key={key} data-error-id={errId}>
                    <CCFieldLabel>{label}</CCFieldLabel>
                    <CCPanel>
                      {/* Fee type selector row */}
                      <CCFeeTypeRow>
                        <CCFeeTypeLabel>{t('Credit Card fee type')}</CCFeeTypeLabel>
                        <CCSelectWrap>
                          <StyledSelect<CreditCardFeeMode>
                            disabled={disabled}
                            value={mode}
                            onChange={(m) => updateValue(product, key, { feeMode: m })}
                            loading={handbook.isLoading}
                            options={creditCardFeeModeOptions}
                          />
                        </CCSelectWrap>
                      </CCFeeTypeRow>

                      {/* Rate inputs row — no MCC Group / Rate header */}
                      <CCRateRow dir={dir}>
                        {showPercent && (
                          <StyledInputNumber
                            min={0}
                            step={0.1}
                            disabled={disabled}
                            addonAfter="%"
                            value={value?.percent ?? undefined}
                            onChange={(v) =>
                              updateValue(product, key, { percent: v == null ? null : Number(v) })
                            }
                            onBlur={() => handleBlur(product, key)}
                          />
                        )}
                        {showPercent && showAmount && <Plus>+</Plus>}
                        {showAmount && (
                          <StyledInputNumber
                            min={0}
                            disabled={disabled}
                            addonBefore={currency}
                            value={value?.amount ?? undefined}
                            onChange={(v) =>
                              updateValue(product, key, { amount: v == null ? null : Number(v) })
                            }
                            onBlur={() => handleBlur(product, key)}
                          />
                        )}
                      </CCRateRow>

                      {meta &&
                        meta.editable &&
                        (meta.minAmount != null || meta.minPercent != null) && (
                          <CCHintText dir={dir}>
                            {t('Minimum')}:{' '}
                            {meta.minPercent != null ? `${meta.minPercent}%` : ''}
                            {meta.minPercent != null && meta.minAmount != null ? ' + ' : ''}
                            {meta.minAmount != null ? `${meta.minAmount} ${currency}` : ''}
                          </CCHintText>
                        )}
                      {errors[errId] && (
                        <CCErrorText dir={dir}>
                          {errors[errId]}
                        </CCErrorText>
                      )}
                    </CCPanel>
                  </FieldRow>
                );
              }

              // ── Standard field ─────────────────────────────────────────────
              return (
                <FieldRow key={key} data-error-id={errId}>
                  <Row gutter={[12, 8]} align="middle">
                    <Col xs={24} sm={10}>
                      <FieldLabel>{label}</FieldLabel>
                    </Col>
                    <Col xs={24} sm={14}>
                      <InputsRow dir={dir}>
                        {showPercent && (
                          <StyledInputNumber
                            min={0}
                            step={0.1}
                            disabled={disabled}
                            addonAfter="%"
                            value={value?.percent ?? undefined}
                            onChange={(v) =>
                              updateValue(product, key, { percent: v == null ? null : Number(v) })
                            }
                            onBlur={() => handleBlur(product, key)}
                          />
                        )}
                        {showPercent && showAmount && <Plus>+</Plus>}
                        {showAmount && (
                          <StyledInputNumber
                            min={0}
                            disabled={disabled}
                            addonBefore={currency}
                            value={value?.amount ?? undefined}
                            onChange={(v) =>
                              updateValue(product, key, { amount: v == null ? null : Number(v) })
                            }
                            onBlur={() => handleBlur(product, key)}
                          />
                        )}
                      </InputsRow>
                      {meta &&
                        meta.editable &&
                        (meta.minAmount != null || meta.minPercent != null) && (
                          <HintText dir={dir}>
                            {t('Minimum')}:{' '}
                            {meta.minPercent != null ? `${meta.minPercent}%` : ''}
                            {meta.minPercent != null && meta.minAmount != null ? ' + ' : ''}
                            {meta.minAmount != null ? `${meta.minAmount} ${currency}` : ''}
                          </HintText>
                        )}
                      {errors[errId] && <ErrorText dir={dir}>{errors[errId]}</ErrorText>}
                    </Col >
                  </Row >
                </FieldRow >
              );
            })}
        </ProductBlock >
      ))}

      <ButtonRow dir={dir}>
        {!isNogoManager && agentCanEditFees && (
          <Button
            type="primary"
            loading={submitMutation.isLoading}
            onClick={handleSubmit}
          >
            {t('Submit for approval')}
          </Button>
        )
        }
        {
          isNogoManager && data.status === 'PENDING' && (
            <ManagerDecision>
              <Select<'APPROVE' | 'REJECT'>
                // style={{ width: 220 }}
                placeholder={t('Approve or reject')}
                value={decision}
                onChange={(d) => setDecision(d)}
                loading={handbook.isLoading}
                options={pricingDecisionOptions}
              />
              {decision === 'REJECT' && (
                <Input.TextArea
                  rows={2}
                  placeholder={t('Reason for rejection')}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              )}
              {decision && (
                <Button
                  type="primary"
                  block
                  loading={approveMutation.isLoading || rejectMutation.isLoading}
                  onClick={decision === 'APPROVE' ? handleApprove : handleReject}
                >
                  {t('Confirm')}
                </Button>
              )}
            </ManagerDecision>
          )
        }
      </ButtonRow >
    </Card >
  );
};

/* ─── Styled components ─────────────────────────────────────── */

const Card = styled.div<DirectionProp>`
background: #ffffff;
border: 1px solid #d9d9d9;
border - radius: 8px;
padding: 16px;
margin - top: 16px;
`;

const Title = styled(Typography.Text)`
display: block;
font - weight: 600;
font - size: 14px;
color: #595959;
margin - bottom: 12px;
padding - bottom: 8px;
border - bottom: 1px solid #f0f0f0;
`;

const ProductBlock = styled.div`
margin - bottom: 16px;
`;

const ProductTitle = styled(Typography.Text)`
display: block;
font - weight: 600;
font - size: 13px;
color: #1a1a2e;
margin - bottom: 8px;
`;

const FieldRow = styled.div`
padding: 10px 0;
border - bottom: 1px solid #f0f0f0;

  &: last - child {
  border - bottom: none;
}
`;

const FieldLabel = styled(Typography.Text)`
font - size: 13px;
font - weight: 500;
color: #1a1a2e;
line - height: 1.4;
`;

const InputsRow = styled.div<DirectionProp>`
display: flex;
align - items: center;
gap: 8px;
flex - wrap: wrap;
  justify-content: ${({ dir }) => (dir === 'rtl' ? 'flex-start' : 'flex-end')};
  direction: ltr;
`;

/**
 * Styled InputNumber — clean border, no spin arrows on hover/disabled,
 * clear disabled state with muted colours.
 */
const StyledInputNumber = styled(InputNumber)`
min - width: 110px;
  direction: ltr;

  /* tighten up the text size */
  .ant - input - number - input {
  font - size: 13px;
    text-align: center;
    direction: ltr;
}

  /* hide spinner arrows — they add noise for fee inputs */
  .ant - input - number - handler - wrap {
  display: none;
}

  /* disabled: grey background, muted text */
  &.ant - input - number - group - wrapper - disabled.ant - input - number - group - addon {
  background: #f5f5f5;
  color: #bfbfbf;
  border - color: #d9d9d9;
}
  &.ant - input - number - group - wrapper - disabled.ant - input - number {
  background: #f5f5f5;
  border - color: #d9d9d9;
}
  &.ant - input - number - group - wrapper - disabled.ant - input - number - input {
  color: #bfbfbf;
  cursor: not - allowed;
}

  /* error state — red border */
  &.error.ant - input - number {
  border - color: #ff4d4f;
  box - shadow: 0 0 0 2px rgba(255, 77, 79, 0.1);
}
`;

/**
 * Styled Select — consistent with StyledInputNumber sizing,
 * clean disabled style.
 */
const StyledSelect = styled(Select)`
min - width: 160px;

  .ant - select - selector {
  font - size: 13px!important;
  border - radius: 6px!important;
}

  /* disabled */
  &.ant - select - disabled.ant - select - selector {
  background: #f5f5f5!important;
  color: #bfbfbf!important;
  border - color: #d9d9d9!important;
  cursor: not - allowed;
}
` as typeof Select;

const Plus = styled.span`
font - weight: 600;
color: #8c8c8c;
flex - shrink: 0;
`;

const HintText = styled.div<DirectionProp>`
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 4px;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'left' : 'right')};
`;

const ErrorText = styled.div<DirectionProp>`
  font-size: 12px;
  color: #ff4d4f;
  margin-top: 4px;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'left' : 'right')};
`;

const StatusAlert = styled(Alert)`
margin - bottom: 12px;
`;

const ButtonRow = styled.div<DirectionProp>`
  display: flex;
  justify-content: ${({ dir }) => (dir === 'rtl' ? 'flex-start' : 'flex-end')};
margin - top: 16px;
padding - top: 12px;
border - top: 1px solid #f0f0f0;
`;

const ManagerDecision = styled.div`
display: flex;
flex - direction: column;
gap: 10px;
width: 100 %;
max - width: 100 %;

  && .ant - select {
  width: 100 % !important;
  max - width: none!important;
}
`;

/**
 * Variant of FieldLabel used above the CC sub-panel — adds bottom margin
 * so the label sits separated from the bordered panel.
 */
const CCFieldLabel = styled(FieldLabel)`
display: block;
margin - bottom: 8px;
`;

/** Hint text inside the CC panel — aligned with text direction and panel padding */
const CCHintText = styled(HintText) <DirectionProp>`
  padding: 0 12px 10px;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'left' : 'right')};
`;

/** Error text inside the CC panel — aligned with text direction and panel padding */
const CCErrorText = styled(ErrorText) <DirectionProp>`
  padding: 0 12px 10px;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'left' : 'right')};
`;

/* ── Credit Card sub-panel ───────────────────────────────────── */

const CCPanel = styled.div`
border: 1px solid #e8e8e8;
border - radius: 6px;
overflow: hidden;
// background: #fafafa;
`;

const CCFeeTypeRow = styled.div`
display: flex;
align - items: center;
  justify-content: space-between;
gap: 16px;
padding: 12px 16px;
border - bottom: 1px solid #e8e8e8;
background: #fafafa;
flex - wrap: wrap;
`;

const CCFeeTypeLabel = styled.div`
flex: 1;
font - size: 13px;
font - weight: 600;
color: #262626;
white - space: nowrap;
min - width: 140px;
`;

/** On mobile the wrapper goes full-width; on larger screens fixed 220px */
const CCSelectWrap = styled.div`
width: 220px;
flex - shrink: 0;

  /* Full width on small screens */
  .ant - select {
  width: 100 % !important;
}

@media(max - width: 575px) {
  width: 100 %;
}
`;

const CCRateRow = styled.div<DirectionProp>`
  display: flex;
  align-items: center;
  justify-content: ${({ dir }) => (dir === 'rtl' ? 'flex-start' : 'flex-end')};
gap: 8px;
padding: 10px 16px;
background: #ffffff;
flex - wrap: wrap;
  direction: ltr;
`;

/* ── End Credit Card sub-panel ───────────────────────────────── */

export default AgreementPricingForm;
